import {
  type CartQuoteLine,
  type CartQuoteResponse,
  type OrderDelivery,
  PHONE_RE,
  type PillowVariantSelection,
  PL_POST_CODE_RE,
  type ProjectSizeCm,
  shippingCentsFor,
  silhouetteTierForDimsCm,
} from "@pufimi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { Check, MapPin, Pencil, Ruler, Trash2 } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { AddMoreMenu } from "@/features/cart/components/AddMoreMenu";
import { CartLineThumbnail, cartLineTitle } from "@/features/cart/components/CartLineRow";
import { SilhouetteSizeSlider } from "@/features/cart/components/SilhouetteSizeSlider";
import { VariantPicker } from "@/features/cart/components/VariantPicker";
import { groupCartLinesByProject } from "@/features/cart/grouping";
import { useCartQuote } from "@/features/cart/useCartQuote";
import { variantPricingQuery } from "@/features/cart/variantPricing";
import { LockerPickerDialog } from "@/features/orders/components/LockerPickerDialog";
import { formatPriceCents } from "@/features/orders/pricing";
import { capture, captureOnce } from "@/lib/analytics";
import { ApiError, api } from "@/lib/api";
import { clientConfigQuery } from "@/lib/clientConfig";
import { i18n } from "@/lib/i18n";
import { appShellTitle } from "@/lib/seo/head";
import { cn } from "@/lib/utils";
import { useCartStore } from "@/stores/useCartStore";

// Basic client-side email sanity check — the server (shared `CheckoutCreateRequest`)
// is the source of truth; this only gates the button so the buyer gets immediate
// feedback. The post-code + phone rules are the SHARED constants the server validates.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type DeliveryMethod = OrderDelivery["method"];

// The delivery form state (#255, #256). Held flat so a method switch keeps whatever the
// buyer already picked/typed; `buildDelivery` narrows it to the `OrderDelivery` union.
interface DeliveryForm {
  method: DeliveryMethod;
  pointCode: string;
  pointName: string;
  pointAddress: string;
  receiverName: string;
  phone: string;
  street: string;
  buildingNumber: string;
  city: string;
  postCode: string;
}

const EMPTY_DELIVERY: DeliveryForm = {
  method: "paczkomat",
  pointCode: "",
  pointName: "",
  pointAddress: "",
  receiverName: "",
  phone: "",
  street: "",
  buildingNumber: "",
  city: "",
  postCode: "",
};

function isDeliveryDestinationSelected(d: DeliveryForm): boolean {
  if (d.method === "paczkomat") return d.pointCode.trim() !== "";
  return (
    d.street.trim() !== "" &&
    d.buildingNumber.trim() !== "" &&
    d.city.trim() !== "" &&
    PL_POST_CODE_RE.test(d.postCode.trim())
  );
}

function isDeliveryComplete(d: DeliveryForm): boolean {
  if (d.receiverName.trim() === "" || !PHONE_RE.test(d.phone.trim())) return false;
  return isDeliveryDestinationSelected(d);
}

function buildDelivery(d: DeliveryForm): OrderDelivery {
  const receiver = { name: d.receiverName.trim(), phone: d.phone.trim() };
  if (d.method === "paczkomat") {
    const name = d.pointName.trim();
    const address = d.pointAddress.trim();
    return {
      method: "paczkomat",
      point: {
        code: d.pointCode.trim(),
        ...(name ? { name } : {}),
        ...(address ? { address } : {}),
      },
      receiver,
    };
  }
  return {
    method: "courier",
    address: {
      street: d.street.trim(),
      buildingNumber: d.buildingNumber.trim(),
      city: d.city.trim(),
      postCode: d.postCode.trim(),
      countryCode: "PL",
    },
    receiver,
  };
}

// The flat shipping cents for the selected method, from the cart quote's per-method
// prices — the SHARED `shippingCentsFor` (also used server-side), so the page and the
// server resolve shipping the same way, with no round-trip on a method toggle.
function quoteShippingCents(method: DeliveryMethod, quote: CartQuoteResponse | null): number {
  return quote ? shippingCentsFor(method, quote.shipping) : 0;
}

// The guest checkout page IS the full Cart (ADR-0054 §3, no separate /cart route): the
// buyer's assembled products grouped by project, editable in place (quantity + remove),
// then one Delivery choice, one Art. 38 waiver, a money summary, and one Pay that opens
// a single Stripe session over the whole cart. Reached from the preview's "Zamów" (which
// adds the product and lands here) or the nav Cart icon. Project-agnostic: the page reads
// the whole browser-local Cart (`useCartStore`) — the URL carries NO project id, since a
// cart can hold products from several projects (ADR-0056; retires the ADR-0051 anchor +
// the browser-local Resume pointer). An empty cart shows the empty state, never an error.
function CheckoutScreen() {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const [status, setStatus] = useState<"idle" | "redirecting">("idle");
  const [errorKey, setErrorKey] = useState<"payDisabled" | "oversize" | "error" | null>(null);
  const [email, setEmail] = useState("");
  const [waiver, setWaiver] = useState(false);
  const [delivery, setDelivery] = useState<DeliveryForm>(EMPTY_DELIVERY);
  const emailId = useId();
  const waiverId = useId();

  const items = useCartStore((s) => s.items);
  const ensureProduct = useCartStore((s) => s.ensureProduct);
  const setQuantity = useCartStore((s) => s.setQuantity);
  const removeProduct = useCartStore((s) => s.removeProduct);

  const queryClient = useQueryClient();
  // The product a variant edit / "add more" is currently mutating (locks that line's
  // pickers + steppers and shows a spinner). A single-flight guard — the cart is small.
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);
  const [variantError, setVariantError] = useState(false);

  // Variant edit (ADR-0054 §2, #405; #425): PATCH the Product's variant, then re-quote — the
  // productId is unchanged, so the cart query key doesn't move and must be invalidated. The
  // `selection` is the shape-discriminated pick — a silhouette carries exact cm, a rectangle a
  // tier; the server derives the price + footprint from it (never a client price).
  const editVariant = useCallback(
    async (line: CartQuoteLine, selection: PillowVariantSelection) => {
      setVariantError(false);
      setPendingProductId(line.productId);
      try {
        await api.products.patchVariant(line.productId, selection);
        await queryClient.invalidateQueries({ queryKey: ["cart-quote"] });
      } catch {
        setVariantError(true);
      } finally {
        setPendingProductId(null);
      }
    },
    [queryClient],
  );

  // "add more" (ADR-0054 §2, #405; #425): create another Product on the same project (config
  // copied from this sibling), then add its id to the cart — the changed item set re-quotes
  // automatically (keepPreviousData holds the tree during the refetch). The `selection` carries
  // exact cm (silhouette) or a tier (rectangle); the server resolves the price + footprint.
  const addMore = useCallback(
    async (line: CartQuoteLine, selection: PillowVariantSelection) => {
      if (!line.projectId) return;
      setVariantError(false);
      setPendingProductId(line.productId);
      try {
        const { productId } = await api.products.create({
          projectId: line.projectId,
          productType: line.productType,
          variant: selection,
        });
        ensureProduct(productId);
      } catch {
        setVariantError(true);
      } finally {
        setPendingProductId(null);
      }
    },
    [ensureProduct],
  );

  // The whole cart, re-priced + re-verified server-side. `null` while loading. Lines the
  // server can no longer quote (GC-reaped, or owned by an expired session) are pruned from
  // the local cart by the hook and reported as `cartExpired`, so a week-old cart heals
  // itself here instead of dead-ending on an error the buyer could never retry away.
  const {
    quote: cartQuote,
    isError: cartLoadError,
    refetch,
    expired: cartExpired,
  } = useCartQuote();

  const patchDelivery = useCallback(
    (patch: Partial<DeliveryForm>) => setDelivery((d) => ({ ...d, ...patch })),
    [],
  );

  useEffect(() => {
    captureOnce("checkout_started", "checkout_started", {});
  }, []);

  const destinationSelected = isDeliveryDestinationSelected(delivery);
  useEffect(() => {
    if (destinationSelected)
      captureOnce(`delivery_selected:${delivery.method}`, "delivery_selected", {
        method: delivery.method,
      });
  }, [destinationSelected, delivery.method]);

  const groups = cartQuote ? groupCartLinesByProject(cartQuote.lines) : [];
  const subtotalCents = cartQuote?.subtotalCents ?? 0;

  // Cart oversize rule (ADR-0054 §4, #406): a cart whose summed shipping-size class exceeds
  // the Paczkomat capacity can't fit a locker, so the server withdraws Paczkomat and forces
  // Kurier. Mirror that on the page — auto-switch a Paczkomat pick to courier so the buyer
  // never submits an unfulfillable delivery, and the notice below explains the switch.
  const oversize = cartQuote?.shipping.oversize ?? false;
  const paczkomatAvailable = cartQuote?.shipping.paczkomatAvailable ?? true;
  useEffect(() => {
    if (oversize && delivery.method === "paczkomat") patchDelivery({ method: "courier" });
  }, [oversize, delivery.method, patchDelivery]);

  const shippingCents = quoteShippingCents(delivery.method, cartQuote);
  const totalCents = subtotalCents + shippingCents;
  const currency = cartQuote?.currency ?? "PLN";
  const maxQuantity = cartQuote?.maxQuantity ?? 1;
  const paymentsEnabled = cartQuote?.paymentsEnabled ?? false;
  const cartEmpty = items.length === 0;

  const emailValid = EMAIL_RE.test(email.trim());
  const deliveryComplete = isDeliveryComplete(delivery);
  const canPay =
    !cartEmpty &&
    cartQuote !== null &&
    emailValid &&
    waiver &&
    deliveryComplete &&
    paymentsEnabled &&
    status === "idle";

  async function onPay() {
    if (!canPay) return;
    setErrorKey(null);
    setStatus("redirecting");
    try {
      const { url } = await api.checkout.create({
        items,
        email: email.trim(),
        withdrawalWaiverAcknowledged: waiver,
        delivery: buildDelivery(delivery),
      });
      capture("payment_initiated", {
        lineCount: items.length,
        method: delivery.method,
        valueCents: totalCents,
      });
      window.location.href = url;
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      setErrorKey(
        code === "payments_disabled"
          ? "payDisabled"
          : code === "paczkomat_unavailable_oversize"
            ? "oversize"
            : "error",
      );
      setStatus("idle");
    }
  }

  return (
    <section className="mx-auto flex w-full max-w-lg flex-col gap-8 py-4">
      <header className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{t("checkout.title")}</h1>
        <p className="mt-2 text-balance text-sm text-muted-foreground">{t("cart.lead")}</p>
      </header>

      {/* Something in the cart had expired and was dropped (see `useCartQuote`). A calm
          notice, not an error: whatever survived is priced below and still orderable, and
          an emptied cart falls through to the empty state's "wgraj zdjęcie" CTA. */}
      {cartExpired ? (
        <p
          role="status"
          className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-center text-sm text-muted-foreground"
          data-testid="checkout-expired-notice"
        >
          {t("cart.expiredNotice")}
        </p>
      ) : null}

      {cartEmpty ? (
        <div
          className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-card p-8 text-center"
          data-testid="checkout-empty"
        >
          <p className="font-medium">{t("cart.empty")}</p>
          <p className="text-sm text-muted-foreground">{t("cart.emptyHint")}</p>
          <Button type="button" onClick={() => void navigate({ to: "/upload" })}>
            {t("cart.emptyCta")}
          </Button>
        </div>
      ) : cartLoadError ? (
        <div
          className="flex flex-col items-center gap-4 rounded-xl border border-border p-6 text-center"
          data-testid="checkout-load-error"
        >
          <p className="text-sm text-muted-foreground">{t("cart.loadError")}</p>
          <Button type="button" variant="outline" onClick={() => void refetch()}>
            {t("checkout.loadRetry")}
          </Button>
        </div>
      ) : (
        <>
          <CartLineList
            groups={groups}
            quote={cartQuote}
            currency={currency}
            maxQuantity={maxQuantity}
            disabled={status === "redirecting"}
            pendingProductId={pendingProductId}
            quantityFor={(id) => items.find((line) => line.productId === id)?.quantity ?? 1}
            onQuantityChange={setQuantity}
            onRemove={removeProduct}
            onEditVariant={editVariant}
            onAddMore={addMore}
            onAddThreeDPrint={ensureProduct}
            t={t}
          />

          <p
            role="status"
            aria-live="polite"
            className={cn(
              "min-h-5 text-center text-sm text-alert transition-opacity",
              variantError ? "opacity-100" : "opacity-0",
            )}
            data-testid="cart-variant-error"
          >
            {variantError ? t("cart.variantError") : ""}
          </p>

          {/* Section transition into the second half of the page (Leon): the cart is
              assembled above; below is where the order is addressed + paid. A calm
              centered heading, not a loud divider — the chrome stays quiet (DESIGN.md). */}
          <h2
            className="text-center font-display text-lg font-semibold tracking-tight text-foreground"
            data-testid="checkout-delivery-heading"
          >
            {t("checkout.deliveryAndPayment")}
          </h2>

          <form
            className="flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void onPay();
            }}
          >
            <div className="flex flex-col gap-2 text-left">
              <Label htmlFor={emailId}>{t("checkout.emailLabel")}</Label>
              <Input
                id={emailId}
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                placeholder={t("checkout.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === "redirecting"}
                data-testid="checkout-email"
              />
            </div>

            <DeliverySection
              value={delivery}
              onChange={patchDelivery}
              quote={cartQuote}
              oversize={oversize}
              paczkomatAvailable={paczkomatAvailable}
              disabled={status === "redirecting"}
              t={t}
            />

            <CartSummary
              subtotalCents={subtotalCents}
              shippingCents={shippingCents}
              totalCents={totalCents}
              currency={currency}
              method={delivery.method}
              ready={cartQuote !== null}
              t={t}
            />

            {/* Art. 38 pkt 3 waiver: a SEPARATE, unticked checkbox (ADR-0020). One waiver
                covers the whole custom order. Pay is blocked until it is ticked. */}
            <label
              htmlFor={waiverId}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-input p-3 text-left text-sm text-foreground/80"
            >
              <input
                id={waiverId}
                type="checkbox"
                className="mt-0.5 size-4 shrink-0 accent-primary"
                checked={waiver}
                onChange={(e) => setWaiver(e.target.checked)}
                disabled={status === "redirecting"}
                data-testid="checkout-waiver"
              />
              <span>{t("checkout.waiver")}</span>
            </label>

            <Button
              type="submit"
              size="lg"
              className="min-h-13 w-full rounded-full text-base font-semibold"
              disabled={!canPay}
              aria-busy={status === "redirecting"}
              data-testid="checkout-pay"
            >
              {status === "redirecting" ? t("checkout.paying") : t("checkout.pay")}
            </Button>

            {cartQuote && !paymentsEnabled ? (
              <p
                className="text-center text-sm text-muted-foreground"
                data-testid="checkout-payments-off"
              >
                {t("checkout.payDisabled")}
              </p>
            ) : null}
          </form>

          <p
            role="status"
            aria-live="polite"
            className={`min-h-5 text-center text-sm text-alert transition-opacity ${errorKey ? "opacity-100" : "opacity-0"}`}
            data-testid="checkout-error"
          >
            {errorKey ? t(`checkout.${errorKey}`) : ""}
          </p>
        </>
      )}
    </section>
  );
}

// The editable line list — the cart's two-level tree grouped by project (ADR-0054 §2):
// each project heads a group, its products underneath with a quantity stepper, a remove,
// and the per-line price. Editing quantity re-quotes server-side (keepPreviousData holds
// the view during the refetch).
function CartLineList({
  groups,
  quote,
  currency,
  maxQuantity,
  disabled,
  pendingProductId,
  quantityFor,
  onQuantityChange,
  onRemove,
  onEditVariant,
  onAddMore,
  onAddThreeDPrint,
  t,
}: {
  groups: ReturnType<typeof groupCartLinesByProject>;
  quote: CartQuoteResponse | null;
  currency: string;
  maxQuantity: number;
  disabled: boolean;
  // The product a variant edit / "add more" is mutating (locks its row's controls).
  pendingProductId: string | null;
  // The stepper reflects the browser-local store quantity (instant on click); the
  // per-line PRICE stays the server quote's (re-priced on the round-trip).
  quantityFor: (productId: string) => number;
  onQuantityChange: (productId: string, quantity: number) => void;
  onRemove: (productId: string) => void;
  onEditVariant: (line: CartQuoteLine, selection: PillowVariantSelection) => void;
  onAddMore: (line: CartQuoteLine, selection: PillowVariantSelection) => void;
  // Add a 3D print of a project's pet to the cart (stub configurator, #407).
  onAddThreeDPrint: (productId: string) => void;
  t: TFunction<"orders">;
}) {
  // Client-safe launch flag (`three_d_print_enabled` via /client-config): gates the
  // "Dodaj Obraz 3D" option in the per-group add-more menu. Fails hidden (false).
  const threeDPrintEnabled = useQuery(clientConfigQuery()).data?.threeDPrintEnabled ?? false;
  if (!quote) {
    return (
      <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-4">
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  const multiProject = groups.length > 1;
  return (
    <div
      className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-4"
      data-testid="checkout-lines"
    >
      {groups.map((group) => (
        <div key={group.projectId ?? "catalog"} className="flex flex-col gap-3">
          {multiProject ? (
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {group.projectId ? t("cart.projectGroup") : t("cart.catalogGroup")}
            </p>
          ) : null}
          {group.lines.map((line) => (
            <div
              key={line.productId}
              className="flex items-center gap-4"
              data-testid="checkout-line"
            >
              {/* Pet render, vertically centered against the line body (Leon): a calm,
                  slightly larger square so the pet reads at a glance without a busy frame. */}
              <CartLineThumbnail line={line} className="size-16" />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="min-w-0 truncate font-medium">{cartLineTitle(line)}</p>
                  <span
                    className="shrink-0 text-sm font-medium tabular-nums"
                    data-testid="checkout-line-price"
                  >
                    {formatPriceCents(line.lineTotalCents, currency)}
                  </span>
                </div>
                {/* Size + quantity share ONE line (Leon): the stepper sits on the right, under
                    the price; the size (+ its inline editor for a silhouette) on the left. The
                    remove affordance lives INSIDE the stepper — at quantity 1 the "−" becomes a
                    trash button with a confirm; there is no separate remove button. */}
                {(() => {
                  const stepper = (
                    <QuantityStepper
                      quantity={quantityFor(line.productId)}
                      onChange={(next) => onQuantityChange(line.productId, next)}
                      onRemove={() => onRemove(line.productId)}
                      maxQuantity={maxQuantity}
                      disabled={disabled || pendingProductId === line.productId}
                      t={t}
                    />
                  );
                  // Silhouette carries the inline exact-cm editor in the size slot (#425): an
                  // edit icon morphs to an accept icon and slides out the slider; the size
                  // changes only on accept. Rectangle/3D print show the plain committed cm.
                  return line.projectId && line.shape === "silhouette" ? (
                    <SilhouetteLineSizeEditor
                      line={line}
                      projectId={line.projectId}
                      pending={pendingProductId === line.productId}
                      disabled={disabled}
                      onCommit={(dimensionsCm) =>
                        onEditVariant(line, { shape: "silhouette", dimensionsCm })
                      }
                      t={t}
                    >
                      {stepper}
                    </SilhouetteLineSizeEditor>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <p
                        className="text-sm text-muted-foreground"
                        data-testid="checkout-line-dimensions"
                      >
                        {t("cart.dimensions", { width: line.widthCm, height: line.heightCm })}
                      </p>
                      {stepper}
                    </div>
                  );
                })()}
                {/* Rectangle is discrete (S/M/L): it keeps the popover picker below the row.
                    Silhouette edits inline above; a 3D print has no size axis (ADR-0054). */}
                {line.projectId && line.shape === "rectangle" ? (
                  <LineVariantActions
                    line={line}
                    projectId={line.projectId}
                    pending={pendingProductId === line.productId}
                    disabled={disabled}
                    onEditVariant={onEditVariant}
                    t={t}
                  />
                ) : null}
              </div>
            </div>
          ))}
          {/* One calm per-pet "add more" menu (Leon): a full-width button that expands to
              "Dodaj rozmiar" + (behind the launch flag) "Dodaj Obraz 3D". Only for a real
              project group (a future catalog group has no pet source). */}
          {group.projectId
            ? (() => {
                // The group's source pillow line — its shape + committed footprint seed the
                // "add more" size controls (the silhouette slider opens at this size, #425).
                const pillowLine = group.lines.find((l) => l.shape) ?? null;
                return (
                  <AddMoreMenu
                    projectId={group.projectId}
                    pillowShape={pillowLine?.shape ?? null}
                    pillowDimsCm={
                      pillowLine ? { width: pillowLine.widthCm, height: pillowLine.heightCm } : null
                    }
                    onAddSize={(selection) => {
                      if (pillowLine) onAddMore(pillowLine, selection);
                    }}
                    onAddThreeDPrint={onAddThreeDPrint}
                    threeDPrintEnabled={threeDPrintEnabled}
                    pending={group.lines.some((l) => l.productId === pendingProductId)}
                    disabled={disabled}
                    t={t}
                  />
                );
              })()
            : null}
        </div>
      ))}
    </div>
  );
}

// The RECTANGLE per-line size EDIT control (ADR-0054 §2, #405): "Zmień rozmiar" opens the
// discrete S/M/L popover for THIS line (three genuinely fixed footprints). Silhouette is
// continuous and edits inline via `SilhouetteLineSizeEditor`; a 3D print has no size axis.
const EDIT_TRIGGER_CLASS =
  "inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none disabled:opacity-60";

// The round icon button beside the silhouette cm readout (Leon): a bold, circular hover
// backdrop. Edit = transparent, the gray circle shows only on hover; accept = a solid primary
// circle so "save" reads at a glance.
const EDIT_ICON_CLASS =
  "inline-flex size-7 shrink-0 items-center justify-center rounded-full text-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40";
const ACCEPT_ICON_CLASS =
  "inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40";

function LineVariantActions({
  line,
  projectId,
  pending,
  disabled,
  onEditVariant,
  t,
}: {
  line: CartQuoteLine;
  projectId: string;
  pending: boolean;
  disabled: boolean;
  onEditVariant: (line: CartQuoteLine, selection: PillowVariantSelection) => void;
  t: TFunction<"orders">;
}) {
  return (
    <div
      className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1"
      data-testid="cart-line-variant-actions"
    >
      <VariantPicker
        projectId={projectId}
        shape="rectangle"
        currentSize={line.sizeCode}
        title={t("cart.editSizeTitle")}
        pending={pending}
        disabled={disabled}
        onPick={(size) => onEditVariant(line, { shape: "rectangle", size })}
        t={t}
      >
        <button type="button" className={EDIT_TRIGGER_CLASS} data-testid="cart-line-edit-variant">
          <Ruler aria-hidden className="size-3.5" />
          {t("cart.editVariant")}
        </button>
      </VariantPicker>
    </div>
  );
}

// The SILHOUETTE per-line size editor (Leon's design, #425): the committed cm readout with an
// edit icon beside it. Pressing it morphs the icon into an accept (✓) icon and slides the cm
// slider out to the right, with a small "Nowy rozmiar: W × H cm" caption below. The line's
// size does NOT change while dragging — it commits (and the slider hides) only on accept.
function SilhouetteLineSizeEditor({
  line,
  projectId,
  pending,
  disabled,
  onCommit,
  t,
  children,
}: {
  line: CartQuoteLine;
  projectId: string;
  pending: boolean;
  disabled: boolean;
  onCommit: (dimensionsCm: ProjectSizeCm) => void;
  t: TFunction<"orders">;
  // The quantity stepper — rendered on the RIGHT of the size row, under the price (Leon).
  children: React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  // The live slider footprint (before accept). Seeded to the committed size when the slider
  // reports its opening position, then tracks the drag — never PATCHed until accept.
  const [draftDimsCm, setDraftDimsCm] = useState<ProjectSizeCm | null>(null);
  const sliderWrapRef = useRef<HTMLDivElement>(null);

  // Focus the thumb when the editor opens so it's immediately keyboard-adjustable. The slider
  // stays mounted (so open + close animate), so focus is tied to the `editing` flip, not mount.
  useEffect(() => {
    if (editing) sliderWrapRef.current?.querySelector<HTMLElement>('[role="slider"]')?.focus();
  }, [editing]);

  const accept = () => {
    if (draftDimsCm) onCommit(draftDimsCm);
    setEditing(false);
  };

  // The caption text holds the live draft; falls back to the committed size so it reads
  // correctly while the row animates closed (before it unmounts).
  const captionDims = draftDimsCm ?? { width: line.widthCm, height: line.heightCm };

  // The "big tier" upgrade surcharge (Leon): once the dragged size crosses into the LARGE box
  // the edit flow shows no price, so a tooltip above the slider calls out the difference (L − S)
  // — otherwise the buyer might not notice the price jumped. Cached pricing (shared with the
  // slider's own query), so this adds no request. The diff text is constant (so it doesn't
  // flicker as the tooltip fades out); `onLargeTier` gates whether it's shown.
  const { data: pricing } = useQuery(variantPricingQuery(projectId));
  const upgradeDiff = pricing
    ? formatPriceCents(
        pricing.pricesCents.silhouette.L - pricing.pricesCents.silhouette.S,
        pricing.currency,
      )
    : null;
  const onLargeTier =
    !!pricing &&
    !!draftDimsCm &&
    silhouetteTierForDimsCm(draftDimsCm, [
      pricing.silhouetteBoxes.smallLongCm,
      pricing.silhouetteBoxes.smallShortCm,
    ]) === "L";
  const showUpgrade = editing && onLargeTier && upgradeDiff !== null;

  return (
    <div className="flex flex-col gap-1" data-testid="cart-line-variant-actions">
      <div className="relative flex items-center justify-between gap-3">
        {/* Big-tier upgrade tooltip — a real floating popover above the slider (absolute, no
            layout cost) on the orange `primary`, shown once the size crosses into the LARGE
            box so the price jump can't slip by. Fades in/out; pointer-transparent. */}
        <div
          className={cn(
            // Anchored to the row's vertical CENTER (= the slider's level, it's `items-center`)
            // and lifted a hair, so it floats just above the slider — not above the whole row.
            "pointer-events-none absolute bottom-1/2 left-1/2 z-20 mb-2.5 -translate-x-1/2 transition-all duration-200 motion-reduce:transition-none",
            showUpgrade ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
          )}
          aria-hidden={!showUpgrade}
        >
          <span
            className="inline-flex items-center whitespace-nowrap rounded-md bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground shadow-md"
            data-testid="cart-line-size-upgrade"
          >
            {t("cart.sizeUpgrade", { price: upgradeDiff ?? "" })}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <p
            className="shrink-0 text-sm text-muted-foreground"
            data-testid="checkout-line-dimensions"
          >
            {t("cart.dimensions", { width: line.widthCm, height: line.heightCm })}
          </p>
          <button
            type="button"
            onClick={editing ? accept : () => setEditing(true)}
            disabled={disabled || pending || (editing && !draftDimsCm)}
            aria-label={editing ? t("cart.confirmSize") : t("cart.editVariant")}
            className={editing ? ACCEPT_ICON_CLASS : EDIT_ICON_CLASS}
            data-testid={editing ? "cart-line-edit-accept" : "cart-line-edit-variant"}
          >
            {editing ? (
              <Check aria-hidden className="size-4" strokeWidth={3} />
            ) : (
              <Pencil aria-hidden className="size-3.5" strokeWidth={2.5} />
            )}
          </button>
          {/* The slider reveals to the RIGHT (and hides) smoothly — a max-width + opacity
              transition, both directions. Kept mounted so the close animates too. The inner
              px-2 keeps the (hover-scaled) thumb off the clipped edges at min/max. */}
          <div
            ref={sliderWrapRef}
            className={cn(
              "min-w-0 flex-1 overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none",
              editing ? "max-w-[600px] opacity-100" : "max-w-0 opacity-0",
            )}
          >
            <div className="px-2">
              <SilhouetteSizeSlider
                projectId={projectId}
                committedDimsCm={{ width: line.widthCm, height: line.heightCm }}
                pending={pending}
                disabled={disabled || !editing}
                showReadout={false}
                onChange={setDraftDimsCm}
                t={t}
              />
            </div>
          </div>
        </div>
        {children}
      </div>
      {/* "Nowy rozmiar" — its own new line, centered, revealing + hiding smoothly under the
          slider (a grid-rows + opacity transition, both directions). */}
      <div
        className={cn(
          "grid transition-all duration-300 ease-out motion-reduce:transition-none",
          editing ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <p
            className="pt-0.5 text-center text-xs text-muted-foreground"
            data-testid="cart-line-new-size"
          >
            {t("cart.newSize", { width: captionDims.width, height: captionDims.height })}
          </p>
        </div>
      </div>
    </div>
  );
}

// The money summary (#255, multi-line): subtotal + the selected method's Dostawa line +
// the recomputed Razem total. Tracks the delivery method live with no round-trip.
function CartSummary({
  subtotalCents,
  shippingCents,
  totalCents,
  currency,
  method,
  ready,
  t,
}: {
  subtotalCents: number;
  shippingCents: number;
  totalCents: number;
  currency: string;
  method: DeliveryMethod;
  ready: boolean;
  t: TFunction<"orders">;
}) {
  if (!ready) return null;
  return (
    <div
      className="flex flex-col gap-1 border-t border-border pt-3 text-sm"
      data-testid="checkout-summary"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">{t("cart.subtotal")}</span>
        <span className="tabular-nums" data-testid="checkout-subtotal">
          {formatPriceCents(subtotalCents, currency)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-4" data-testid="checkout-shipping">
        <span className="text-muted-foreground">
          {t("checkout.summary.shipping")} · {t(`checkout.delivery.method${methodKey(method)}`)}
        </span>
        <span className="tabular-nums">{formatPriceCents(shippingCents, currency)}</span>
      </div>
      <div
        className="mt-1 flex items-center justify-between gap-4 font-medium"
        data-testid="checkout-total"
      >
        <span>{t("checkout.summary.total")}</span>
        <span className="tabular-nums">{formatPriceCents(totalCents, currency)}</span>
      </div>
    </div>
  );
}

// "Paczkomat" | "Courier" → the i18n key suffix (`methodPaczkomat` / `methodCourier`).
function methodKey(method: DeliveryMethod): "Paczkomat" | "Courier" {
  return method === "paczkomat" ? "Paczkomat" : "Courier";
}

// The InPost "Dostawa" section (#255, #256): a Paczkomat (default) / Kurier InPost radio
// showing BOTH per-method prices, then the chosen method's destination — the Geowidget
// locker-picker map for the paczkomat or the PL address form for the courier — plus the
// receiver name + required phone. One Delivery for the whole order (ADR-0054 §4).
function DeliverySection({
  value,
  onChange,
  quote,
  oversize,
  paczkomatAvailable,
  disabled,
  t,
}: {
  value: DeliveryForm;
  onChange: (patch: Partial<DeliveryForm>) => void;
  quote: CartQuoteResponse | null;
  // Cart oversize rule (#406): when the order can't fit a locker, Paczkomat is greyed out
  // and un-selectable, and a notice explains the forced switch to Kurier InPost.
  oversize: boolean;
  paczkomatAvailable: boolean;
  disabled: boolean;
  t: TFunction<"orders">;
}) {
  const ids = {
    receiverName: useId(),
    phone: useId(),
    street: useId(),
    building: useId(),
    city: useId(),
    postCode: useId(),
  };
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <fieldset className="flex flex-col gap-3 text-left" data-testid="checkout-delivery">
      <legend className="mb-1 text-sm font-medium">{t("checkout.delivery.sectionTitle")}</legend>

      <div className="grid grid-cols-2 gap-2">
        <MethodOption
          method="paczkomat"
          label={t("checkout.delivery.methodPaczkomat")}
          priceCents={quote?.shipping.paczkomatCents ?? null}
          currency={quote?.shipping.currency ?? null}
          selected={value.method === "paczkomat"}
          disabled={disabled || !paczkomatAvailable}
          onSelect={() => onChange({ method: "paczkomat" })}
        />
        <MethodOption
          method="courier"
          label={t("checkout.delivery.methodCourier")}
          priceCents={quote?.shipping.courierCents ?? null}
          currency={quote?.shipping.currency ?? null}
          selected={value.method === "courier"}
          disabled={disabled}
          onSelect={() => onChange({ method: "courier" })}
        />
      </div>

      {/* Oversize notice (#406): the cart is too big for a locker, so the buyer is on
          Kurier — a calm, on-brand line, not an error. */}
      {oversize ? (
        <p
          className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
          role="status"
          aria-live="polite"
          data-testid="checkout-oversize-notice"
        >
          {t("checkout.delivery.oversizeNotice")}
        </p>
      ) : null}

      {value.method === "paczkomat" ? (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t("checkout.delivery.lockerLabel")}</span>
          {value.pointCode ? (
            <div
              className="flex items-start justify-between gap-3 rounded-lg border p-3"
              data-testid="checkout-locker-selected"
            >
              <div className="flex min-w-0 items-start gap-2.5">
                <MapPin className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                <span className="flex min-w-0 flex-col">
                  <span className="text-sm font-medium" data-testid="checkout-locker-code">
                    {t("checkout.delivery.lockerCard", { code: value.pointCode })}
                  </span>
                  {value.pointName ? <span className="text-sm">{value.pointName}</span> : null}
                  {value.pointAddress ? (
                    <span className="text-xs text-muted-foreground">{value.pointAddress}</span>
                  ) : null}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPickerOpen(true)}
                disabled={disabled}
                data-testid="checkout-locker-change"
              >
                {t("checkout.delivery.changeLocker")}
              </Button>
            </div>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPickerOpen(true)}
                disabled={disabled}
                data-testid="checkout-locker-open"
              >
                <MapPin aria-hidden />
                {t("checkout.delivery.pickLockerCta")}
              </Button>
              <p className="text-xs text-muted-foreground">{t("checkout.delivery.lockerHint")}</p>
            </>
          )}
          <LockerPickerDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            t={t}
            onSelect={(locker) =>
              onChange({
                pointCode: locker.code,
                pointName: locker.name ?? "",
                pointAddress: locker.address ?? "",
              })
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-3 flex flex-col gap-2">
            <Label htmlFor={ids.street}>{t("checkout.delivery.streetLabel")}</Label>
            <Input
              id={ids.street}
              name="address-line1"
              autoComplete="shipping address-line1"
              value={value.street}
              onChange={(e) => onChange({ street: e.target.value })}
              disabled={disabled}
              data-testid="checkout-delivery-street"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={ids.building}>{t("checkout.delivery.buildingNumberLabel")}</Label>
            <Input
              id={ids.building}
              name="address-line2"
              autoComplete="shipping address-line2"
              value={value.buildingNumber}
              onChange={(e) => onChange({ buildingNumber: e.target.value })}
              disabled={disabled}
              data-testid="checkout-delivery-building"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={ids.postCode}>{t("checkout.delivery.postCodeLabel")}</Label>
            <Input
              id={ids.postCode}
              name="postal-code"
              inputMode="numeric"
              autoComplete="shipping postal-code"
              placeholder={t("checkout.delivery.postCodePlaceholder")}
              value={value.postCode}
              onChange={(e) => onChange({ postCode: e.target.value })}
              disabled={disabled}
              data-testid="checkout-delivery-postcode"
            />
          </div>
          <div className="col-span-3 flex flex-col gap-2">
            <Label htmlFor={ids.city}>{t("checkout.delivery.cityLabel")}</Label>
            <Input
              id={ids.city}
              name="city"
              autoComplete="shipping address-level2"
              value={value.city}
              onChange={(e) => onChange({ city: e.target.value })}
              disabled={disabled}
              data-testid="checkout-delivery-city"
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor={ids.receiverName}>{t("checkout.delivery.receiverNameLabel")}</Label>
        <Input
          id={ids.receiverName}
          name="name"
          autoComplete="shipping name"
          placeholder={t("checkout.delivery.receiverNamePlaceholder")}
          value={value.receiverName}
          onChange={(e) => onChange({ receiverName: e.target.value })}
          disabled={disabled}
          data-testid="checkout-delivery-receiver-name"
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={ids.phone}>{t("checkout.delivery.phoneLabel")}</Label>
        <Input
          id={ids.phone}
          name="tel"
          type="tel"
          inputMode="tel"
          autoComplete="shipping tel"
          placeholder={t("checkout.delivery.phonePlaceholder")}
          value={value.phone}
          onChange={(e) => onChange({ phone: e.target.value })}
          disabled={disabled}
          data-testid="checkout-delivery-phone"
        />
        <p className="text-xs text-muted-foreground">{t("checkout.delivery.phoneHint")}</p>
      </div>
    </fieldset>
  );
}

// One method radio card — a native radio wrapped in a label so the whole card is
// clickable, showing the method name + its flat price.
function MethodOption({
  method,
  label,
  priceCents,
  currency,
  selected,
  disabled,
  onSelect,
}: {
  method: DeliveryMethod;
  label: string;
  priceCents: number | null;
  currency: string | null;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-lg border p-3",
        selected ? "border-primary ring-1 ring-primary" : "border-input",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        type="radio"
        name="delivery-method"
        value={method}
        className="size-4 shrink-0 accent-primary"
        checked={selected}
        onChange={onSelect}
        disabled={disabled}
        data-testid={`checkout-delivery-${method}`}
      />
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {priceCents !== null && currency ? formatPriceCents(priceCents, currency) : "—"}
        </span>
      </span>
    </label>
  );
}

// The per-line quantity stepper (#228): bounded to [1, maxQuantity] (`checkout_max_quantity`,
// #240). At the minimum (Leon) the "−" turns into a REMOVE affordance — a trash button that
// opens a confirm dialog — so dropping the last one is deliberate; there is no separate remove
// button on the row. Above the minimum it decrements as usual.
function QuantityStepper({
  quantity,
  onChange,
  onRemove,
  maxQuantity,
  disabled,
  t,
}: {
  quantity: number;
  onChange: (next: number) => void;
  onRemove: () => void;
  maxQuantity: number;
  disabled: boolean;
  t: TFunction<"orders">;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const clamp = (n: number) => Math.min(maxQuantity, Math.max(1, n));
  const atMin = quantity <= 1;
  return (
    <div className="mt-1 flex items-center gap-1" data-testid="checkout-quantity">
      {atMin ? (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 rounded-full text-muted-foreground transition-colors hover:border-alert/40 hover:text-alert"
              aria-label={t("cart.remove")}
              disabled={disabled}
              data-testid="checkout-line-remove"
            >
              <Trash2 aria-hidden className="size-4" />
            </Button>
          </DialogTrigger>
          <DialogContent
            className="max-w-sm gap-4 rounded-2xl"
            data-testid="checkout-remove-dialog"
          >
            <DialogHeader>
              <DialogTitle>{t("cart.removeConfirm.title")}</DialogTitle>
              <DialogDescription>{t("cart.removeConfirm.body")}</DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline" className="rounded-full">
                  {t("cart.removeConfirm.cancel")}
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                className="rounded-full"
                onClick={() => {
                  setConfirmOpen(false);
                  onRemove();
                }}
                data-testid="checkout-remove-confirm"
              >
                {t("cart.removeConfirm.confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8 rounded-full text-base"
          aria-label={t("checkout.quantityDecrease")}
          disabled={disabled}
          onClick={() => onChange(clamp(quantity - 1))}
          data-testid="checkout-quantity-dec"
        >
          −
        </Button>
      )}
      <output
        className="w-8 text-center text-sm font-semibold tabular-nums"
        data-testid="checkout-quantity-value"
      >
        {quantity}
      </output>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-8 rounded-full text-base"
        aria-label={t("checkout.quantityIncrease")}
        disabled={disabled || quantity >= maxQuantity}
        onClick={() => onChange(clamp(quantity + 1))}
        data-testid="checkout-quantity-inc"
      >
        +
      </Button>
    </div>
  );
}

export const Route = createFileRoute("/checkout/")({
  head: () => ({ meta: [{ title: appShellTitle(i18n.t("shells.checkout")) }] }),
  component: CheckoutScreen,
});
